/**
 * Staff Console -> Teams (Enterprise teams, 2026-10-03).
 *
 * Client orgs for the Custom / Enterprise plan: every rep is their own
 * DealCloser user with their own LinkedIn, grouped here under one org.
 * Staff create the org, attach reps (by email or user id), pick who is a
 * manager (managers get the /team console), and bill the org per seat with
 * one Stripe subscription.
 */

import { useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { Building2, Check, Copy, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtLocale } from '@/lib/dates'
import { isStaffUser } from './staff'
import {
  useAddTeamMember,
  useCreateTeamOrg,
  useOrgSeatCheckout,
  useRemoveTeamMember,
  useSetTeamRole,
  useSyncOrgSeats,
  useTeamOrg,
  useTeamOrgs,
  type SeatSync,
  type TeamOrg,
} from './use-admin-teams'

function errText(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error'
}

function seatSyncNote(s: SeatSync | undefined): string {
  if (!s || s.status === 'not_billed') return ''
  if (s.status === 'synced') return ` Seats billed: ${s.seat_count ?? '?'}.`
  return ` Seat sync ${s.status}${s.detail ? `: ${s.detail}` : ''}. Use Sync seats.`
}

function CreateOrgCard({ onCreated }: { onCreated: (id: string) => void }) {
  const [name, setName] = useState('')
  const create = useCreateTeamOrg()
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">New client team</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-2 sm:flex-row"
          onSubmit={async (e) => {
            e.preventDefault()
            if (!name.trim()) return
            try {
              const res = await create.mutateAsync({ name: name.trim() })
              setName('')
              onCreated(res.org.id)
            } catch (err) {
              alert(`Could not create the team: ${errText(err)}`)
            }
          }}
        >
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Client name, e.g. Acme Lending" className="h-9 sm:max-w-sm" />
          <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
            {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Create team
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function SeatBillingCard({ org, canAct }: { org: TeamOrg; canAct: boolean }) {
  const [plan, setPlan] = useState<'starter' | 'pro'>('pro')
  const [email, setEmail] = useState('')
  const [seats, setSeats] = useState('')
  const [seatPrice, setSeatPrice] = useState('')
  const [link, setLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const checkout = useOrgSeatCheckout()
  const sync = useSyncOrgSeats()
  const billed = Boolean(org.stripe_subscription_id) && org.billing_status !== 'cancelled'

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Seat billing</CardTitle>
        <p className="text-[11px] text-[var(--color-silver)]">
          One Stripe subscription for the whole team (Elevated Talent account). Quantity follows the number of reps
          (comp accounts are not billed), so one invoice covers every seat.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <span>
            Status: <Badge className="ml-1 capitalize text-[10px]">{org.billing_status ?? 'not billed'}</Badge>
          </span>
          <span>Plan: <span className="capitalize text-[var(--color-silver-bright)]">{org.seat_plan ?? '—'}</span></span>
          <span>Seats billed: <span className="font-mono text-[var(--color-silver-bright)]">{org.seat_count ?? 0}</span></span>
          <span>Reps: <span className="font-mono text-[var(--color-silver-bright)]">{org.member_count}</span></span>
          {org.billing_email && <span>Invoices to: {org.billing_email}</span>}
        </div>

        {billed && canAct && (
          <Button
            size="sm"
            variant="outline"
            disabled={sync.isPending}
            onClick={async () => {
              try {
                const r = await sync.mutateAsync({ orgId: org.id })
                alert(r.changed ? `Seats updated to ${r.seat_count}.` : `Seats already match (${r.seat_count}).`)
              } catch (err) {
                alert(`Sync failed: ${errText(err)}`)
              }
            }}
          >
            {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Sync seats
          </Button>
        )}

        {!billed && canAct && (
          <form
            className="grid grid-cols-1 gap-2 sm:grid-cols-[120px_1fr_100px_130px_auto]"
            onSubmit={async (e) => {
              e.preventDefault()
              try {
                const r = await checkout.mutateAsync({
                  orgId: org.id,
                  plan,
                  billingEmail: email.trim(),
                  seats: seats ? Number(seats) : undefined,
                  seatPriceUsd: seatPrice ? Number(seatPrice) : undefined,
                })
                setLink(r.url)
              } catch (err) {
                alert(`Could not create the checkout link: ${errText(err)}`)
              }
            }}
          >
            <Select value={plan} onValueChange={(v) => setPlan(v as 'starter' | 'pro')}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="starter">Basic</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
              </SelectContent>
            </Select>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Billing email" className="h-9" type="email" />
            <Input
              value={seats}
              onChange={(e) => setSeats(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder={`Seats (${Math.max(1, org.member_count)})`}
              className="h-9"
              inputMode="numeric"
            />
            <Input
              value={seatPrice}
              onChange={(e) => setSeatPrice(e.target.value.replace(/[^0-9.]/g, ''))}
              placeholder={`$/seat/mo (${plan === 'pro' ? '69' : '29'})`}
              title="Price per seat per month, set from this client's usage. Blank = the plan's list price."
              className="h-9"
              inputMode="decimal"
            />
            <Button type="submit" size="sm" disabled={checkout.isPending || !email.trim()}>
              {checkout.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Create checkout link
            </Button>
          </form>
        )}

        {link && (
          <div className="flex items-center gap-2 rounded-md border border-[rgba(16,185,129,0.25)] bg-[rgba(16,185,129,0.06)] p-2">
            <code className="flex-1 truncate text-xs">{link}</code>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                await navigator.clipboard.writeText(link).catch(() => undefined)
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              }}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function OrgDetail({ orgId, canAct }: { orgId: string; canAct: boolean }) {
  const { data, isLoading } = useTeamOrg(orgId)
  const [target, setTarget] = useState('')
  const [role, setRole] = useState<'manager' | 'member'>('member')
  const add = useAddTeamMember()
  const setMemberRole = useSetTeamRole()
  const remove = useRemoveTeamMember()

  if (isLoading || !data) {
    return (
      <div className="flex items-center py-6 text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading team…
      </div>
    )
  }
  const { org, members } = data
  const billed = Boolean(org.stripe_subscription_id) && org.billing_status !== 'cancelled'

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{org.name} · {members.length} rep{members.length === 1 ? '' : 's'}</CardTitle>
          <p className="text-[11px] text-[var(--color-silver)]">
            Reps in one team never message the same LinkedIn profile, and an opt-out to any rep blocks that person for
            the whole team. Managers get the Team console at /team.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {canAct && (
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={async (e) => {
                e.preventDefault()
                if (!target.trim()) return
                try {
                  const r = await add.mutateAsync({ orgId: org.id, userIdOrEmail: target, role })
                  setTarget('')
                  const moved = r.moved_from.length ? ' They were moved out of their previous team.' : ''
                  const conflicts = r.claims.conflicts
                    ? ` ${r.claims.conflicts} of their leads are already held by a teammate and will not be messaged.`
                    : ''
                  alert(`Added.${moved}${conflicts}${seatSyncNote(r.seats)}`)
                } catch (err) {
                  alert(`Could not add: ${errText(err)}`)
                }
              }}
            >
              <Input
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder="Rep's email or user id (they must have signed up)"
                className="h-9 sm:max-w-sm"
              />
              <Select value={role} onValueChange={(v) => setRole(v as 'manager' | 'member')}>
                <SelectTrigger className="h-9 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Rep</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" size="sm" disabled={add.isPending || !target.trim()}>
                {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Add to team
              </Button>
            </form>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rep</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Seat</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[40px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-sm text-[var(--color-silver)]">
                    No reps yet. Add them by email above.
                  </TableCell>
                </TableRow>
              )}
              {members.map((m) => {
                const onSeat = billed && m.stripe_subscription_id === org.stripe_subscription_id
                return (
                  <TableRow key={m.user_id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-[var(--color-silver-bright)]">{m.full_name || '—'}</span>
                        <span className="text-xs text-[var(--color-silver)]">{m.email ?? m.user_id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {canAct && (m.role === 'manager' || m.role === 'member') ? (
                        <Select
                          value={m.role}
                          onValueChange={async (v) => {
                            try {
                              await setMemberRole.mutateAsync({ orgId: org.id, userId: m.user_id, role: v as 'manager' | 'member' })
                            } catch (err) {
                              alert(`Could not change the role: ${errText(err)}`)
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 w-28 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Rep</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className="capitalize text-[10px]">{m.role === 'member' ? 'rep' : m.role}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs capitalize">
                      {m.is_comp === 1 ? 'comp' : `${m.plan ?? '—'} · ${m.plan_status ?? '—'}`}
                    </TableCell>
                    <TableCell className="text-xs">
                      {m.is_comp === 1 ? 'not billed' : onSeat ? (
                        <span className="text-emerald-300">on team seat</span>
                      ) : billed ? (
                        <span className="text-amber-300" title="Has a personal subscription or was added before payment. Sync seats, or cancel their personal plan first.">
                          not on seat
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-[var(--color-silver)]">{fmtLocale(m.joined_at, 'date')}</TableCell>
                    <TableCell>
                      {canAct && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Remove from team"
                          onClick={async () => {
                            const warning = onSeat
                              ? `\n\nThis ends their paid team seat: their account is cancelled, their campaigns pause, and their LinkedIn is disconnected from DealCloser.`
                              : ''
                            if (!confirm(`Remove ${m.email ?? m.user_id} from ${org.name}?${warning}`)) return
                            try {
                              const r = await remove.mutateAsync({ orgId: org.id, userId: m.user_id })
                              const note = seatSyncNote(r.seats)
                              if (note) alert(`Removed.${note}`)
                            } catch (err) {
                              alert(`Could not remove: ${errText(err)}`)
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <SeatBillingCard org={org} canAct={canAct} />
    </div>
  )
}

export function TeamsPage() {
  const { user } = useUser()
  const canAct = isStaffUser(user?.id)
  const { data: orgs, isLoading } = useTeamOrgs()
  const [selected, setSelected] = useState<string | null>(null)
  const current = selected ?? orgs?.[0]?.id ?? null

  return (
    <div className="space-y-4">
      {canAct && <CreateOrgCard onCreated={setSelected} />}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
        <Card>
          <CardContent className="p-2">
            {isLoading && (
              <div className="flex items-center p-3 text-sm text-[var(--color-silver)]">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
              </div>
            )}
            {!isLoading && (orgs ?? []).length === 0 && (
              <p className="p-3 text-xs text-[var(--color-silver)]">No client teams yet.</p>
            )}
            {(orgs ?? []).map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setSelected(o.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                  current === o.id
                    ? 'bg-[rgba(16,185,129,0.12)] text-[#34D399]'
                    : 'text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.04)]',
                )}
              >
                <Building2 className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{o.name}</span>
                <span className="font-mono text-xs">{o.member_count}</span>
              </button>
            ))}
          </CardContent>
        </Card>
        {current ? <OrgDetail orgId={current} canAct={canAct} /> : <div />}
      </div>
    </div>
  )
}
